import { useEffect, useState } from "react";
import { Search, UserRound } from "lucide-react";
import {
  resolveHandlePreselect,
  useMessagesDirectory,
  type MessageDirectoryMember,
} from "@/lib/messages";
import { RoleChip } from "@/components/messages/ConversationRow";

export function ColleagueSearch({
  citySlug,
  selected,
  requestedHandle = "",
  requestedUserId = "",
  onSelect,
}: {
  citySlug: string;
  selected: MessageDirectoryMember | null;
  requestedHandle?: string;
  requestedUserId?: string;
  onSelect: (member: MessageDirectoryMember | null) => void;
}) {
  const [query, setQuery] = useState(requestedHandle.replace(/^@/, ""));
  const [debouncedQuery, setDebouncedQuery] = useState(query);
  const directory = useMessagesDirectory(citySlug, debouncedQuery);

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedQuery(query.trim()), 250);
    return () => window.clearTimeout(timeout);
  }, [query]);

  useEffect(() => {
    if (selected || !directory.data) return;
    const match = requestedUserId
      ? directory.data.members.find((member) => member.userId === requestedUserId) ?? null
      : resolveHandlePreselect(directory.data.members, requestedHandle);
    if (match) onSelect(match);
  }, [directory.data, onSelect, requestedHandle, requestedUserId, selected]);

  if (selected) {
    return (
      <section className="pb-messages-selected-colleague" aria-label="Selected colleague">
        <UserRound size={20} aria-hidden="true" />
        <span>
          <strong>{selected.display}</strong>
          <small>
            @{selected.handle}{selected.specialty ? ` · ${selected.specialty}` : ""}
          </small>
        </span>
        <RoleChip role={selected.role} />
        {selected.existingConversationId ? <small>Existing conversation</small> : null}
        <button type="button" onClick={() => onSelect(null)}>Change</button>
      </section>
    );
  }

  return (
    <section className="pb-messages-colleague-search" aria-labelledby="colleague-search-heading">
      <header>
        <h2 id="colleague-search-heading">Choose a colleague</h2>
        <p>Search message-enabled members in {citySlug === "canberra" ? "Canberra" : "your city"}.</p>
      </header>
      <label>
        <Search size={15} aria-hidden="true" />
        <input
          type="search"
          value={query}
          autoFocus={!requestedHandle && !requestedUserId}
          placeholder="Name, @handle or specialty"
          aria-label="Search colleagues"
          onChange={(event) => setQuery(event.target.value)}
        />
      </label>

      {directory.isLoading ? (
        <p className="pb-messages-search-state" role="status">Finding colleagues…</p>
      ) : directory.isError ? (
        <p className="pb-messages-search-state pb-messages-search-state--error" role="alert">
          Colleagues could not be loaded. <button type="button" onClick={() => void directory.refetch()}>Retry</button>
        </p>
      ) : directory.data?.members.length ? (
        <ul>
          {directory.data.members.map((member) => (
            <li key={member.userId}>
              <button type="button" onClick={() => onSelect(member)}>
                <UserRound size={16} aria-hidden="true" />
                <span>
                  <strong>{member.display}</strong>
                  <small>@{member.handle}{member.specialty ? ` · ${member.specialty}` : ""}</small>
                </span>
                <RoleChip role={member.role} />
                {member.existingConversationId ? <small>Open</small> : null}
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="pb-messages-search-state">
          {debouncedQuery ? "No messageable colleague matches that search." : "No other colleagues are available yet."}
        </p>
      )}
    </section>
  );
}
